import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Badge, 
  Form,
  Alert,
  Spinner,
  Modal,
  Tab,
  Tabs
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagic,
  faSearch,
  faBell,
  faBellSlash,
  faTrash,
  faEdit,
  faPlus,
  faRobot,
  faLightbulb,
  faUserCircle,
  faHome,
  faMapMarkerAlt,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import './SavedSearches.css';

interface SavedSearch {
  id: string;
  name: string;
  criteria: any;
  alert_enabled: boolean;
  created_at: string;
  last_run: string;
  results_count?: number;
}

interface AIContext {
  hasProfile: boolean;
  preferences: any;
  buyerStage: string;
  timeframe: string;
}

interface AIRecommendation {
  title: string;
  reason: string;
  criteria: any;
}

interface SavedSearchesProps {
  user: any;
}

const SavedSearches: React.FC<SavedSearchesProps> = ({ user }) => {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [aiContext, setAiContext] = useState<AIContext | null>(null);
  const [recommendations, setRecommendations] = useState<AIRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewSearch, setShowNewSearch] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiThinking, setAiThinking] = useState(false);
  const [activeTab, setActiveTab] = useState('saved');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      // Use either customer portal token or main auth token
      const authToken = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${authToken}` };

      // Try to load AI context (may fail for main portal users)
      try {
        const contextResponse = await axiosInstance.get(
          `/customer-portal/ai-context/${user.id}`,
          { headers }
        );
        setAiContext(contextResponse.data.context);
      } catch (error) {
        console.log('AI context not available for this user type');
      }

      // Load saved searches
      console.log('Loading saved searches for user:', user);
      console.log('Auth token exists:', !!authToken);
      const searchesResponse = await axiosInstance.get(
        `/mls-portal/saved-searches`,
        { headers }
      );
      console.log('Saved searches response:', searchesResponse.data);
      // Map backend fields to component fields
      const mappedSearches = (searchesResponse.data.data || []).map((search: any) => ({
        id: search.id,
        name: search.search_name,
        criteria: search.search_criteria,
        alert_enabled: search.alert_frequency !== 'never',
        created_at: search.created_at,
        last_run: search.last_run || search.created_at,
        results_count: search.results_count
      }));
      setSavedSearches(mappedSearches);

      // Try to load AI recommendations (may fail for main portal users)
      try {
        const recsResponse = await axiosInstance.get(
          `/customer-portal/ai-recommendations/${user.id}`,
          { headers }
        );
        setRecommendations(recsResponse.data.recommendations || []);
      } catch (error) {
        console.log('AI recommendations not available for this user type');
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAISearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setAiThinking(true);
    try {
      const authToken = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${authToken}` };

      const response = await axiosInstance.post(
        '/customer-portal/ai-search',
        {
          customerId: user.id,
          query: aiQuery,
          searchContext: aiContext,
          previousSearches: savedSearches.map(s => s.name)
        },
        { headers }
      );

      if (response.data.success) {
        // Show AI explanation
        alert(`AI Says: ${response.data.explanation}`);
        
        // Create a new saved search from AI results
        const searchName = `AI: ${aiQuery.substring(0, 30)}...`;
        await saveSearch(searchName, response.data.criteria);
      }
    } catch (error) {
      console.error('AI search error:', error);
    } finally {
      setAiThinking(false);
      setAiQuery('');
    }
  };

  const saveSearch = async (name: string, criteria: any) => {
    try {
      const authToken = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${authToken}` };

      await axiosInstance.post(
        '/mls-portal/saved-searches',
        {
          search_name: name,
          search_criteria: criteria,
          alert_frequency: 'daily'
        },
        { headers }
      );

      // Reload searches
      await loadData();
      setShowNewSearch(false);
    } catch (error) {
      console.error('Error saving search:', error);
    }
  };

  const toggleAlerts = async (searchId: string, enabled: boolean) => {
    // TODO: Implement alert toggle
    console.log('Toggle alerts:', searchId, enabled);
  };

  const deleteSearch = async (searchId: string) => {
    if (!window.confirm('Delete this saved search?')) return;
    // TODO: Implement delete
    console.log('Delete search:', searchId);
  };

  const renderAIAssistant = () => (
    <Card className="ai-assistant-card mb-4">
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon icon={faRobot} size="2x" className="text-primary me-3" />
          <div>
            <h5 className="mb-0">AI Property Search Assistant</h5>
            <small className="text-muted">
              {aiContext?.hasProfile 
                ? "I know your preferences! Let's find your perfect home."
                : "Tell me what you're looking for and I'll help you search."}
            </small>
          </div>
        </div>

        {aiContext && !aiContext.hasProfile && (
          <Alert variant="info">
            <FontAwesomeIcon icon={faLightbulb} className="me-2" />
            I'm still learning about your preferences. The more you search, the better I can help!
          </Alert>
        )}

        <Form onSubmit={handleAISearch}>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Example: 3 bedroom house with pool under 600k in good school district"
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              disabled={aiThinking}
              className="ai-search-input"
            />
          </Form.Group>
          <Button 
            type="submit" 
            variant="primary" 
            disabled={!aiQuery.trim() || aiThinking}
            className="w-100"
          >
            {aiThinking ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                AI is thinking...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faMagic} className="me-2" />
                Search with AI
              </>
            )}
          </Button>
        </Form>

        {aiContext?.preferences && (
          <div className="mt-3 p-3 bg-light rounded">
            <small className="text-muted d-block mb-2">Based on your profile:</small>
            <div className="d-flex flex-wrap gap-2">
              {aiContext.preferences.location?.length > 0 && (
                <Badge bg="secondary">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                  {aiContext.preferences.location.join(', ')}
                </Badge>
              )}
              <Badge bg="secondary">
                <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                ${aiContext.preferences.priceRange.min.toLocaleString()} - 
                ${aiContext.preferences.priceRange.max.toLocaleString()}
              </Badge>
              <Badge bg="secondary">
                <FontAwesomeIcon icon={faHome} className="me-1" />
                {aiContext.preferences.bedrooms}+ bed, {aiContext.preferences.bathrooms}+ bath
              </Badge>
              {aiContext.buyerStage && (
                <Badge bg="info">Stage: {aiContext.buyerStage}</Badge>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  const renderRecommendations = () => (
    <Card className="recommendations-card mb-4">
      <Card.Header>
        <h6 className="mb-0">
          <FontAwesomeIcon icon={faLightbulb} className="me-2" />
          AI Recommendations for You
        </h6>
      </Card.Header>
      <Card.Body>
        {recommendations.map((rec, index) => (
          <div key={index} className="recommendation-item mb-3 p-3 border rounded">
            <h6>{rec.title}</h6>
            <small className="text-muted d-block mb-2">{rec.reason}</small>
            <Button 
              size="sm" 
              variant="outline-primary"
              onClick={() => saveSearch(rec.title, rec.criteria)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Save This Search
            </Button>
          </div>
        ))}
      </Card.Body>
    </Card>
  );

  const renderSavedSearches = () => (
    <div>
      {savedSearches.length === 0 ? (
        <Alert variant="info">
          <FontAwesomeIcon icon={faSearch} className="me-2" />
          No saved searches yet. Use the AI assistant above to create your first search!
        </Alert>
      ) : (
        savedSearches.map((search) => (
          <Card key={search.id} className="saved-search-card mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6>{search.name}</h6>
                  <div className="search-criteria mb-2">
                    {Object.entries(search.criteria).map(([key, value]) => (
                      <Badge key={key} bg="light" text="dark" className="me-2">
                        {key}: {String(value)}
                      </Badge>
                    ))}
                  </div>
                  <small className="text-muted">
                    Created {new Date(search.created_at).toLocaleDateString()}
                    {search.results_count !== undefined && (
                      <span className="ms-2">• {search.results_count} properties</span>
                    )}
                  </small>
                </div>
                <div className="search-actions">
                  <Button
                    size="sm"
                    variant={search.alert_enabled ? "success" : "secondary"}
                    onClick={() => toggleAlerts(search.id, !search.alert_enabled)}
                    className="me-2"
                  >
                    <FontAwesomeIcon 
                      icon={search.alert_enabled ? faBell : faBellSlash} 
                    />
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => deleteSearch(search.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))
      )}
    </div>
  );

  if (loading) {
    return (
      <Container className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your searches...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="saved-searches-container p-4">
      <Row>
        <Col lg={8} className="mx-auto">
          <h3 className="mb-4">
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Saved Searches & AI Assistant
          </h3>

          {renderAIAssistant()}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'saved')}
            className="mb-3"
          >
            <Tab eventKey="saved" title="Your Saved Searches">
              {renderSavedSearches()}
            </Tab>
            <Tab eventKey="recommendations" title="AI Recommendations">
              {renderRecommendations()}
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default SavedSearches;